// ============================================================================
// STORE.TOUCHES.JS — Messaging Touch Log (Messaging v1)
//
// Writes clipboard-first messaging touches to the shared reworkTouches
// subcollection (schemaVersion: 2), distinct from the operational rework
// touches written by store.rework.js (schemaVersion: 1 / no schemaVersion).
//
// Path: /tenants/{tenantId}/referrals/{referralId}/reworkTouches/{touchId}
//
// Zero-PHI invariants:
//   • No patient name / DOB / MRN / contact in any field.
//   • touchType, channel, status are controlled enums.
//   • missingItemTypeLabels are human-readable item type labels (not patient data).
//   • notYetReasons are engine-derived criteria labels (not patient data).
//
// Invariants:
//   1. Each createTouchLog call writes exactly one reworkTouches doc.
//   2. createdAt is always serverTimestamp() (satisfies Firestore rule).
//   3. actorUid always matches the authenticated user (rule-enforced).
//   4. schemaVersion is always 2 (distinguishes from operational v1 touches).
// ============================================================================

import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  setDoc,
  getDocs,
  collection,
  query,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';

// ── DB singleton ─────────────────────────────────────────────────────────────

function getDb() {
  return getFirestore(getApp());
}

// ── ID generator ─────────────────────────────────────────────────────────────

function generateTouchId() {
  return `mtch_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ============================================================================
// ENUMS
// ============================================================================

/** Type of message generated (distinguishes the 3 messaging templates). */
export const TOUCH_TYPE = {
  REFERRER_MESSAGE:    'REFERRER_MESSAGE',
  PATIENT_MESSAGE:     'PATIENT_MESSAGE',
  NOT_YET_EXPLANATION: 'NOT_YET_EXPLANATION',
};

/** Delivery channel — clipboard-first for v1; extend for email/fax in v2. */
export const TOUCH_CHANNEL = {
  CLIPBOARD: 'CLIPBOARD',
};

/** What the user did with the generated message. */
export const TOUCH_STATUS = {
  COPIED: 'COPIED', // copied to clipboard
  SENT:   'SENT',   // user manually marked as sent
};

/** Human-readable labels for touch type display in history. */
export const TOUCH_TYPE_LABELS = {
  REFERRER_MESSAGE:    'Referrer Information Request',
  PATIENT_MESSAGE:     'Patient Notification',
  NOT_YET_EXPLANATION: 'Not-Yet Explanation',
};

/** Human-readable labels for touch status. */
export const TOUCH_STATUS_LABELS = {
  COPIED: 'Copied',
  SENT:   'Marked Sent',
};

// ============================================================================
// WRITE
// ============================================================================

/**
 * Append a messaging touch log entry to reworkTouches (schemaVersion: 2).
 *
 * @param {object} params
 * @param {string}   params.tenantId            - Tenant ID
 * @param {string}   params.referralId          - Referral ID
 * @param {string}   params.touchType           - TOUCH_TYPE enum value
 * @param {string}   params.channel             - TOUCH_CHANNEL enum value
 * @param {string}   params.status              - TOUCH_STATUS enum value
 * @param {string[]} params.missingItemTypeLabels - Item labels included in message
 * @param {string[]} params.notYetReasons        - Reason labels included in message
 * @param {string}   params.actorUid            - UID of acting user (must match auth)
 * @param {string}   params.actorRole           - Role of acting user
 * @returns {Promise<{ touchId: string }>}
 */
export async function createTouchLog({
  tenantId,
  referralId,
  touchType,
  channel,
  status,
  missingItemTypeLabels = [],
  notYetReasons = [],
  actorUid,
  actorRole,
}) {
  const touchId = generateTouchId();
  const db = getDb();
  const ref = doc(db, PATHS.reworkTouch(tenantId, referralId, touchId));

  await setDoc(ref, {
    touchId,
    tenantId,
    referralId,
    touchType,
    channel,
    status,
    missingItemTypeLabels,
    notYetReasons,
    actorUid,
    createdByUid:  actorUid,
    actorRole,
    createdAt:     serverTimestamp(),
    schemaVersion: 2,
  });

  return { touchId };
}

// ============================================================================
// READ
// ============================================================================

/**
 * List messaging touch log entries (schemaVersion: 2) for a referral.
 * Results are sorted newest-first and filtered client-side by schemaVersion.
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @param {number} [maxResults=30]
 * @returns {Promise<Array>}
 */
export async function listMessagingTouches(tenantId, referralId, maxResults = 30) {
  const db = getDb();
  const q = query(
    collection(db, PATHS.col.reworkTouches(tenantId, referralId)),
    orderBy('createdAt', 'desc'),
    limit(maxResults),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((t) => t.schemaVersion === 2);
}
